/* eslint-disable no-underscore-dangle */
import { makeAutoObservable } from 'mobx';
import { isPast, isToday, isUpcoming, isWithinHours } from '@helpers/dateUtils';
import {
  Team,
  TeamImage,
  TeamPlayer,
  TeamRole,
  Schedule,
  TeamGameResponse,
} from './team.d';

import axios from '@helpers/axios';
import * as Sentry from '@sentry/react';
export default class TeamStore {
  private initialState = {
    id: '',
    createdTs: 0,
    embedNameFirstLevel: 0,
    embedNameLastLevel: 0,
    isEmbedImageVideo: false,
    isEmbedImagePublicWebsite: false,
    isNameOnPublicWebsite: false,
    publicPublishLevel: 0,
    ageLevel: '',
    imageLogo: { category: '', large: '', small: '' },
    nameLocation: '',
    nameHandle: '',
    nameLong: '',
    roles: [],
    sport: '',
    createdBecause: '',
    imageCover: { category: '', large: '', small: '' },
    players: [],
    nameMed: '',
    nameShort: '',
    nameHandleLower: '',
    season: 0,
    zipCode: '',
    isAge13Plus: false,
    colorPrimary: '',
    invitePhoneNumberList: [],
    inviteAdminPhoneNumberList: [],
    isArchived: false,
    isNotifyCommunityTeamPlayerUpdates: false,
    overlaySettings: {},
    publishCrosspostMode: '',
    scoreTeamIdList: [],
    accountFeatures: { pro: false },
    schedule: {
      upcoming: [],
      past: [],
      today: [],
    },
    pin: null,
    scoringService: 'sscore-v001',
    socialInstagramHandle: '',
    socialFacebookUrl: '',
    socialTwitterHandle: '',
  };

  id: string = this.initialState.id;
  createdTs: number = this.initialState.createdTs;
  embedNameFirstLevel: number = this.initialState.embedNameFirstLevel;
  embedNameLastLevel: number = this.initialState.embedNameLastLevel;
  isEmbedImageVideo: boolean = this.initialState.isEmbedImageVideo;
  isEmbedImagePublicWebsite: boolean =
    this.initialState.isEmbedImagePublicWebsite;
  isNameOnPublicWebsite: boolean = this.initialState.isNameOnPublicWebsite;
  publicPublishLevel: number = this.initialState.publicPublishLevel;
  ageLevel: string = this.initialState.ageLevel;
  imageLogo: TeamImage = this.initialState.imageLogo;
  nameLocation: string = this.initialState.nameLocation;
  nameHandle: string = this.initialState.nameHandle;
  nameLong: string = this.initialState.nameLong;
  roles: string[] = this.initialState.roles;
  sport: string = this.initialState.sport;
  createdBecause: string = this.initialState.createdBecause;
  imageCover: TeamImage = this.initialState.imageCover;
  players: TeamPlayer[] = this.initialState.players;
  nameMed: string = this.initialState.nameMed;
  nameShort: string = this.initialState.nameShort;
  nameHandleLower: string = this.initialState.nameHandleLower;
  season: number = this.initialState.season;
  zipCode: string = this.initialState.zipCode;
  isAge13Plus: boolean = this.initialState.isAge13Plus;
  colorPrimary: string = this.initialState.colorPrimary;
  invitePhoneNumberList: string[] = this.initialState.invitePhoneNumberList;
  inviteAdminPhoneNumberList: string[] =
    this.initialState.inviteAdminPhoneNumberList;
  isArchived: boolean = this.initialState.isArchived;
  isNotifyCommunityTeamPlayerUpdates: boolean =
    this.initialState.isNotifyCommunityTeamPlayerUpdates;
  overlaySettings: Record<string, unknown> = this.initialState.overlaySettings;
  publishCrosspostMode: string = this.initialState.publishCrosspostMode;
  scoreTeamIdList: string[] = this.initialState.scoreTeamIdList;
  accountFeatures = this.initialState.accountFeatures;
  schedule: Schedule = {
    upcoming: [],
    past: [],
    today: [],
  };
  pin: string | null = null;
  scoringService: string = 'sscore-v001';
  socialInstagramHandle: string = this.initialState.socialInstagramHandle;
  socialFacebookUrl: string = this.initialState.socialFacebookUrl;
  socialTwitterHandle: string = this.initialState.socialTwitterHandle;

  constructor() {
    makeAutoObservable(this);
  }

  resetTeamStore() {
    Object.assign(this, this.initialState);
  }

  setTeamInfo(team: Team) {
    this.id = team.id;
    this.createdTs = team.createdTs || 0;
    this.nameLong = team.nameLong;
    this.embedNameFirstLevel = team.embedNameFirstLevel;
    this.embedNameLastLevel = team.embedNameLastLevel;
    this.isEmbedImageVideo = team.isEmbedImageVideo;
    this.isEmbedImagePublicWebsite = team.isEmbedImagePublicWebsite;
    this.isNameOnPublicWebsite = team.isNameOnPublicWebsite;
    this.publicPublishLevel = team.publicPublishLevel;
    this.ageLevel = team.ageLevel;
    this.imageLogo = team.imageLogo ?? { category: '', large: '', small: '' };
    this.nameLocation = team.nameLocation;
    this.nameHandle = team.nameHandle;
    this.nameLong = team.nameLong;
    this.roles = team.roles;
    this.sport = team.sport;
    this.imageCover = team.imageCover ?? { category: '', large: '', small: '' };
    this.players = team.players || [];
    this.nameMed = team.nameMed;
    this.nameShort = team.nameShort;
    this.nameHandleLower = team.nameHandle.toLowerCase();
    this.season = team.season || 0;
    this.zipCode = team.zipCode || '';
    this.isAge13Plus = team.isAge13Plus || false;
    this.colorPrimary = team.colorPrimary || '';
    this.invitePhoneNumberList = team.invitePhoneNumberList || [];
    this.inviteAdminPhoneNumberList = team.inviteAdminPhoneNumberList || [];
    this.isArchived = team.isArchived;
    this.isNotifyCommunityTeamPlayerUpdates =
      team.isNotifyCommunityTeamPlayerUpdates || false;
    this.overlaySettings = { ...team.overlaySettings };
    this.publishCrosspostMode = team.publishCrosspostMode || '';
    this.scoreTeamIdList = team.scoreTeamIdList || [];
    this.accountFeatures = team.accountFeatures || { pro: false };
    this.scoringService = team.scoringService || 'sscore-v001';
    this.pin = team.pin || null;
    this.socialInstagramHandle = team.socialInstagramHandle || '';
    this.socialFacebookUrl = team.socialFacebookUrl || '';
    this.socialTwitterHandle = team.socialTwitterHandle || '';
  }

  setSchedule(schedule: Schedule) {
    this.schedule = schedule;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setPrivacySettings(settings: Partial<Team>) {
    this.isAge13Plus = settings.isAge13Plus || false;
    this.isNameOnPublicWebsite = settings.isNameOnPublicWebsite || false;
    this.isEmbedImagePublicWebsite =
      settings.isEmbedImagePublicWebsite || false;
    this.isEmbedImageVideo = settings.isEmbedImageVideo || false;
    this.embedNameFirstLevel = settings.embedNameFirstLevel || 0;
    this.embedNameLastLevel = settings.embedNameLastLevel || 0;
    this.publicPublishLevel = settings.publicPublishLevel || 0;
    this.isNotifyCommunityTeamPlayerUpdates =
      settings.isNotifyCommunityTeamPlayerUpdates || false;
  }

  setImage(cateogory: string, image: TeamImage) {
    if (cateogory === '100') {
      this.imageLogo = image;
    } else if (cateogory === '102') {
      this.imageCover = image;
    }
  }

  async getTeamInfo(teamId: string) {
    await axios
      .get(`/v2/teams/${teamId}`)
      .then((res) => {
        const data = res.data;
        this.setTeamInfo(data);
      })
      .catch((err) => {
        Sentry.captureException(err);
      });
  }
  async getGameScheule(teamId: string) {
    await axios
      .get(`/v2/teams/${teamId}/games`)
      .then((response) => {
        const games = response.data;

        const past = games.filter((x: TeamGameResponse) =>
          isPast(x.scheduleTsStart)
        );
        const upcoming = games.filter((x: TeamGameResponse) =>
          isUpcoming(x.scheduleTsStart)
        );

        const today = games.filter((x: TeamGameResponse) =>
          isToday(x.scheduleTsStart)
        );

        this.setSchedule({ past, upcoming, today });
      })
      .catch((err) => {
        Sentry.captureException(err);
      });
  }

  resetSchedule() {
    this.schedule = {
      upcoming: [],
      past: [],
      today: [],
    };
  }

  get isTeamManager() {
    return this.roles.includes(TeamRole.ADMIN);
  }

  get isTeamCoach() {
    return this.roles.includes(TeamRole.COACH);
  }

  get isTeamFollower() {
    return this.roles.includes(TeamRole.FOLLOWER);
  }

  get fetchTeamInfo() {
    return {
      createdTs: this.createdTs,
      nameLong: this.nameLong,
      nameMed: this.nameMed,
      nameShort: this.nameShort,
      nameHandle: this.nameHandle,
      nameLocation: this.nameLocation,
      sport: this.sport,
      season: this.season,
      ageLevel: this.ageLevel,
      id: this.id,
      imageCover: this.imageCover,
      imageLogo: this.imageLogo,
      scoringService: this.scoringService,
      pin: this.pin,
      embedNameFirstLevel: this.embedNameFirstLevel,
      embedNameLastLevel: this.embedNameLastLevel,
      isNameOnPublicWebsite: this.isNameOnPublicWebsite,
      publicPublishLevel: this.publicPublishLevel,
      isEmbedImageVideo: this.isEmbedImageVideo,
      isEmbedImagePublicWebsite: this.isEmbedImagePublicWebsite,
      isAge13Plus: this.isAge13Plus,
      zipCode: this.zipCode,
      socialInstagramHandle: this.socialInstagramHandle,
      socialFacebookUrl: this.socialFacebookUrl,
      socialTwitterHandle: this.socialTwitterHandle,
      publishCrosspostMode: this.publishCrosspostMode,
      colorPrimary: this.colorPrimary,
    };
  }

  get fetchPrivacySettings() {
    return {
      isAge13Plus: this.isAge13Plus,
      isNameOnPublicWebsite: this.isNameOnPublicWebsite,
      isEmbedImagePublicWebsite: this.isEmbedImagePublicWebsite,
      isEmbedImageVideo: this.isEmbedImageVideo,
      embedNameFirstLevel: this.embedNameFirstLevel,
      embedNameLastLevel: this.embedNameLastLevel,
      publicPublishLevel: this.publicPublishLevel,
      isNotifyCommunityTeamPlayerUpdates:
        this.isNotifyCommunityTeamPlayerUpdates,
    };
  }

  get fetchOverlaySettings() {
    return { ...this.overlaySettings };
  }

  get fetchTeamPlayers() {
    return this.players.map((player: TeamPlayer) => ({
      id: player.id,
      nameFirst: player.nameFirst,
      nameLast: player.nameLast,
      jerseyNum: player.jerseyNum,
      embedNameFirstLevel: player.embedNameFirstLevel,
      embedNameLastLevel: player.embedNameLastLevel,
      externalData: player.externalData,
    }));
  }

  get fetchSchedule() {
    const past = this.schedule.past;
    const upcoming = this.schedule.upcoming;
    const today = this.schedule.today;
    return {
      past: past
        .slice()
        .sort(
          (a: TeamGameResponse, b: TeamGameResponse) =>
            new Date(a.scheduleTsStart).getTime() -
            new Date(b.scheduleTsStart).getTime()
        ),
      upcoming: upcoming
        .slice()
        .sort(
          (a: TeamGameResponse, b: TeamGameResponse) =>
            new Date(a.scheduleTsStart).getTime() -
            new Date(b.scheduleTsStart).getTime()
        ),
      today: today
        .slice()
        .sort(
          (a: TeamGameResponse, b: TeamGameResponse) =>
            new Date(a.scheduleTsStart).getTime() -
            new Date(b.scheduleTsStart).getTime()
        ),
    };
  }

  // add a game to the schedule
  updateSchedule(teamGame: TeamGameResponse) {
    let updateToday = this.schedule.today;
    let updateUpcoming = this.schedule.upcoming;
    let updatePast = this.schedule.past;
    if (isToday(teamGame.scheduleTsStart)) {
      updateToday = [...updateToday, teamGame];
    } else if (isPast(teamGame.scheduleTsStart)) {
      updatePast = [...updatePast, teamGame];
    } else {
      updateUpcoming = [...updateUpcoming, teamGame];
    }
    this.schedule = {
      past: updatePast,
      upcoming: updateUpcoming,
      today: updateToday,
    };
  }
  // updates an existing game in the schedule
  updateGame(teamGame: TeamGameResponse) {
    let updateToday = this.schedule.today;
    let updateUpcoming = this.schedule.upcoming;
    let updatePast = this.schedule.past;
    let fullList = [...updateToday, ...updateUpcoming, ...updatePast];

    fullList = fullList.map((game) => {
      return game.id === teamGame.id ? teamGame : game;
    });

    updatePast = fullList.filter((x: TeamGameResponse) =>
      isPast(x.scheduleTsStart)
    );

    updateUpcoming = fullList.filter((x: TeamGameResponse) =>
      isUpcoming(x.scheduleTsStart)
    );

    updateToday = fullList.filter((x: TeamGameResponse) =>
      isToday(x.scheduleTsStart)
    );

    this.schedule = {
      past: updatePast,
      upcoming: updateUpcoming,
      today: updateToday,
    };
  }

  deleteGame(teamGameId: string) {
    let updateUpcoming = this.schedule.upcoming;
    let updateToday = this.schedule.today;
    let updatePast = this.schedule.past;
    const fullList = [...updateToday, ...updateUpcoming, ...updatePast];
    const updatedFull = fullList.filter((game) => game.id !== teamGameId);

    updatePast = updatedFull.filter((x: TeamGameResponse) =>
      isPast(x.scheduleTsStart)
    );

    updateUpcoming = updatedFull.filter((x: TeamGameResponse) =>
      isUpcoming(x.scheduleTsStart)
    );

    updateToday = updatedFull.filter((x: TeamGameResponse) =>
      isToday(x.scheduleTsStart)
    );

    this.schedule = {
      past: [...updatePast],
      upcoming: [...updateUpcoming],
      today: [...updateToday],
    };
  }

  get nextStreamableGame() {
    const updateUpcoming = this.schedule.upcoming;
    const updateToday = this.schedule.today;
    const updatePast = this.schedule.past;
    const fullList = [...updateToday, ...updateUpcoming, ...updatePast];
    const canGoLive = fullList
      .filter(
        (x: TeamGameResponse) => isWithinHours(x.scheduleTsStart) && !x.isEnded
      )
      .sort((a, b) => a.scheduleTsStart - b.scheduleTsStart);
    return canGoLive[0];
  }
}
